import heic2any from 'heic2any';

/**
 * Checks if a file is a HEIC/HEIF file
 * @param {File} file - The file to check
 * @returns {boolean} - True if the file is HEIC/HEIF
 */
export const isHEICFile = (file) => {
  if (!file || !file.name) return false;
  
  const fileName = file.name.toLowerCase();
  return fileName.endsWith('.heic') || fileName.endsWith('.heif');
};

/**
 * Converts a HEIC/HEIF file to JPEG
 * @param {File} file - The HEIC/HEIF file to convert
 * @param {number} quality - JPEG quality (0.1 to 1.0, default 0.9)
 * @returns {Promise<File>} - The converted JPEG file
 */
export const convertHEICToJPEG = async (file, quality = 0.9) => {
  try {
    console.log('Converting HEIC file:', file.name);
    
    const convertedBlob = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: quality
    });
    
    // Create new file name with .jpg extension
    const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    
    // Create new File object from converted blob
    const convertedFile = new File([convertedBlob], newFileName, {
      type: 'image/jpeg',
      lastModified: file.lastModified
    });
    
    console.log('HEIC conversion successful:', {
      original: file.name,
      converted: newFileName,
      originalSize: file.size,
      convertedSize: convertedFile.size
    });
    
    return convertedFile;
  } catch (error) {
    console.error('HEIC conversion failed:', error);
    throw new Error(`Failed to convert HEIC file ${file.name}: ${error.message}`);
  }
};

/**
 * Processes multiple files, converting HEIC files to JPEG and leaving others as-is
 * @param {FileList|File[]} files - Array of files to process
 * @param {number} quality - JPEG quality for HEIC conversion (0.1 to 1.0, default 0.9)
 * @returns {Promise<File[]>} - Array of processed files
 */
export const processFiles = async (files, quality = 0.9) => {
  const fileArray = Array.from(files);
  const processedFiles = [];
  
  for (const file of fileArray) {
    try {
      if (isHEICFile(file)) {
        // Convert HEIC to JPEG
        const convertedFile = await convertHEICToJPEG(file, quality);
        processedFiles.push(convertedFile);
      } else {
        // Keep non-HEIC files as-is
        processedFiles.push(file);
      }
    } catch (error) {
      console.error('Error processing file:', file.name, error);
      // You can choose to either skip the file or throw an error
      // For now, we'll skip failed conversions but keep other files
      console.warn(`Skipping file ${file.name} due to conversion error`);
    }
  }
  
  return processedFiles;
};

/**
 * Creates a preview URL for a file, handling HEIC conversion if needed
 * @param {File} file - The file to create preview for
 * @returns {Promise<string>} - The preview URL (blob URL)
 */
export const createPreviewURL = async (file) => {
  try {
    if (isHEICFile(file)) {
      // Convert HEIC to JPEG for preview
      const convertedFile = await convertHEICToJPEG(file, 0.8); // Lower quality for preview
      return URL.createObjectURL(convertedFile);
    } else {
      // Create preview directly for non-HEIC files
      return URL.createObjectURL(file);
    }
  } catch (error) {
    console.error('Error creating preview for:', file.name, error);
    // Return empty string or placeholder image URL on error
    return '';
  }
};